/** Thẻ trạng thái dùng cho ma trận (rút gọn từ customer-tags) */
export interface TagOption {
    id: number;
    name: string;
    code?: string | null;
    color?: string | null;
    /**
     * Thẻ sale được xếp nhóm (vd "group_1"); thẻ trạng thái lịch hẹn thì để trống.
     * Dùng để loại thẻ sale khỏi danh sách chọn, nhưng vẫn giữ lại để tra tên.
     */
    group?: string | null;
}

/** Thẻ sale (có `group`) không thuộc luồng lịch hẹn nên không cho chọn vào ma trận. */
export function isAppointmentTag(tag: TagOption): boolean {
    return !tag.group;
}

/** Một đích đến trong hàng của trạng thái nguồn */
export interface MatrixDest {
    /** ID bản ghi trên server. Chưa có = đích mới thêm, chưa lưu. */
    id?: number;
    toTagId: number;
    isAllowed: boolean;
}

/** Một hàng của ma trận: trạng thái nguồn + các đích được phép chọn */
export interface MatrixRow {
    fromTagId: number;
    dests: MatrixDest[];
}

export type DiffKind = 'add' | 'remove' | 'enable' | 'disable' | 'add-row' | 'remove-row';

/** Hai loại `*-row` là thay đổi ở cấp hàng, không gắn với đích nào. */
export const ROW_DIFF_KINDS: DiffKind[] = ['add-row', 'remove-row'];

export interface DiffEntry {
    kind: DiffKind;
    fromTagId: number;
    /** Với thay đổi cấp hàng, giá trị này bằng `fromTagId` và không mang ý nghĩa. */
    toTagId: number;
}

export function isRowDiff(entry: DiffEntry): boolean {
    return ROW_DIFF_KINDS.includes(entry.kind);
}

/** Màu mặc định khi thẻ trạng thái chưa được gán màu */
export const FALLBACK_TAG_COLOR = '#3e91ab';

/** Ghép mã màu hex với độ trong suốt, dùng cho nền chip */
export function withAlpha(color: string | null | undefined, alpha: string): string {
    const hex = (color || FALLBACK_TAG_COLOR).trim();
    return /^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}${alpha}` : `${FALLBACK_TAG_COLOR}${alpha}`;
}

export function tagColor(color: string | null | undefined): string {
    const hex = (color || '').trim();
    return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : FALLBACK_TAG_COLOR;
}

/**
 * Màu chữ của chip. Nhiều thẻ dùng màu đậm (xanh rêu, xanh navy) đọc được trên
 * nền sáng nhưng chìm hẳn trên nền tối, nên ở dark mode ta pha thêm trắng.
 */
export function readableTagColor(color: string | null | undefined, isDark: boolean): string {
    const hex = tagColor(color);
    if (!isDark) return hex;

    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    if (luminance >= 0.6) return hex;

    const ratio = luminance < 0.32 ? 0.66 : 0.42;
    const lift = (c: number) => Math.round(c + (255 - c) * ratio);
    return `#${[lift(r), lift(g), lift(b)]
        .map((v) => v.toString(16).padStart(2, '0'))
        .join('')}`;
}

/** Nền chip: nền tối cần đậm hơn một chút mới thấy được khối màu. */
export function chipBackground(color: string | null | undefined, isDark: boolean): string {
    return withAlpha(tagColor(color), isDark ? '30' : '1f');
}

/**
 * Tên thẻ đã bỏ phần mã ở đầu. Dữ liệu thật đặt tên kiểu "S5 - Đã đặt lịch",
 * nếu hiện cả chip mã lẫn tên đầy đủ thì mã bị lặp hai lần.
 */
export function tagShortName(tag: TagOption | undefined, fallbackId: number): string {
    if (!tag) return `Thẻ #${fallbackId}`;
    const code = (tag.code || '').trim();
    if (!code) return tag.name;

    // So khớp bỏ qua dấu chấm/gạch: code "S8" vẫn khớp tên "S.8 - Hoàn Thành"
    const normalize = (s: string) => s.replace(/[^a-z0-9]/gi, '').toLowerCase();
    const head = tag.name.match(/^\s*([A-Za-z0-9.\-_]+)\s*(?:[-–—:]\s*)?/);
    if (head && normalize(head[1]) === normalize(code)) {
        const stripped = tag.name.slice(head[0].length).trim();
        if (stripped) return stripped;
    }
    return tag.name;
}
